import clsx from 'clsx';
import * as React from 'react';
import { Flex, Nav, Navbar } from '../';
import { Row } from '../grid';
import { NavbarVerticalMenuItem } from './navbar-vertical-menu-item';
import { NavbarVerticalMenu } from './navbar-vertical-menu';
import { useAppShellTheme } from './theme-provider';
import ToggleButton from './toggle-button';

interface AppShellContextValue {
  isFluid?: boolean;
}

const AppShellContext = React.createContext<AppShellContextValue>({
  isFluid: false,
});

interface AppShellProps extends React.PropsWithChildren {
  isFluid?: boolean;
  className?: string;
}

const AppShellRoot = ({
  children,
  isFluid: isFluidProp,
  className,
}: AppShellProps) => {
  const theme = useAppShellTheme();
  const isFluid = isFluidProp ?? theme.isFluid;
  const contextValue = React.useMemo(() => ({ isFluid }), [isFluid]);

  return (
    <AppShellContext.Provider value={contextValue}>
      <div
        className={clsx(isFluid ? 'container-fluid' : 'container', className)}
      >
        {children}
      </div>
    </AppShellContext.Provider>
  );
};

interface AppShellHeaderProps extends React.PropsWithChildren {
  className?: string;
  // showDropShadow?: boolean;
  expand?: string | boolean;
}

const AppShellHeader = ({
  className,
  // showDropShadow = false,
  expand = true,
  children,
}: AppShellHeaderProps) => {
  const [showDropShadow, setShowDropShadow] = React.useState(false);

  const setDropShadow = () => {
    const el = document.documentElement;
    setShowDropShadow(el.scrollTop > 0);
  };

  React.useEffect(() => {
    window.addEventListener('scroll', setDropShadow);
    return () => window.removeEventListener('scroll', setDropShadow);
  }, []);

  return (
    <Navbar
      className={clsx(
        'navbar-glass fs-xxs navbar-top fixed-navbar sticky-kit',
        {
          'navbar-glass-shadow': showDropShadow,
        },
        className,
      )}
      expand={expand}
    >
      {children}
    </Navbar>
  );
};

interface AppShellNavbarProps extends React.PropsWithChildren {
  className?: string;
  visible?: boolean;
  expand?: string | boolean;
  variant?: 'light' | 'dark';
  showBurgerMenu?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  toggleButton?: React.ReactNode;
}

const AppShellNavbar = ({
  className,
  visible: visibleProp,
  expand: expandProp,
  variant = 'light',
  showBurgerMenu: showBurgerMenuProp,
  onMouseEnter,
  onMouseLeave,
  toggleButton,
  children,
}: AppShellNavbarProps) => {
  const theme = useAppShellTheme();
  const visible = visibleProp ?? true;
  const expand = expandProp ?? theme.navbarBreakPoint;
  const showBurgerMenu = showBurgerMenuProp ?? theme.showBurgerMenu;

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  React.useEffect(() => {
    if (theme.isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [theme.isNavbarVerticalCollapsed, HTMLClassList]);

  const handleMouseEnter = React.useCallback(() => {
    if (theme.isNavbarVerticalCollapsed) {
      const time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
      return () => clearTimeout(time);
    }
    onMouseEnter?.();
  }, [theme.isNavbarVerticalCollapsed, HTMLClassList, onMouseEnter]);

  const handleMouseLeave = React.useCallback(() => {
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
    onMouseLeave?.();
  }, [HTMLClassList, onMouseLeave]);

  if (!visible) return null;

  return (
    <Navbar
      expand={expand}
      className={clsx('navbar-vertical navbar-inverted', className)}
      variant={variant}
    >
      {toggleButton && (
        <Flex className='fixed-navbar' alignItems='center'>
          {toggleButton}
        </Flex>
      )}
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div className='navbar-vertical-content scrollbar'>
          <Nav className='flex-column' as='ul'>
            {children}
          </Nav>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

interface AppShellMainProps extends React.PropsWithChildren {
  className?: string;
}

const AppShellMain = ({ children, className }: AppShellMainProps) => {
  return <div className={clsx('main-content', className)}>{children}</div>;
};

interface AppShellFooterProps extends React.PropsWithChildren {
  className?: string;
}

const AppShellFooter = ({ className, children }: AppShellFooterProps) => {
  return (
    <footer className={clsx('footer', className)}>
      <Row className='justify-content-between text-center fs-xxs mt-4 mb-3'>
        {children}
      </Row>
    </footer>
  );
};

export const AppShell = Object.assign(AppShellRoot, {
  Header: AppShellHeader,
  ToggleButton: ToggleButton,
  Navbar: AppShellNavbar,
  Menu: NavbarVerticalMenu,
  Item: NavbarVerticalMenuItem,
  Main: AppShellMain,
  Footer: AppShellFooter,
});

export { AppShellContext };
